// The account's projects: the list from the relay (kept as it came, sealed), opened with the vault key in memory — its
// own projects (`GET /api/projects`) and, from W9a, the projects shared with it (`GET /api/shares`), each addressed as
// `<owner>/<projectId>` (client/net/address.ts) so nothing kept for one can be taken for another.
import type { Api } from '@writing-suite/client/net/api.ts';
import { addressOf } from '@writing-suite/client/net/address.ts';
import { listShares, type Share } from '@writing-suite/client/net/shares.ts';
import { openShare, type ShareRole } from '@writing-suite/client/net/sharing.ts';
import type { WebProject } from '@writing-suite/client/net/vault.ts';
import { fromB64u } from '@writing-suite/client/shared/b64.ts';
import { openSummary, unwrapProjectKey, type ProjectSummary } from '@writing-suite/client/shared/crypto.ts';
import type { ExchangeKey } from '@writing-suite/client/shared/members.ts';
import type { OfflineStore, ProjectInfo } from './offline.ts';

/** How a project shared with this account stands (W9a). */
export interface SharedInfo {
  owner: string;
  ownerName: string;
  ownerPublicKey: string | null;
  projectId: string;
  role: ShareRole;
  /** `removed`: the owner removed this account; `deleted`: the owner deleted the project or their account. Either way
   *  the copy on this computer still reads, for exporting. */
  status: 'active' | 'removed' | 'deleted';
}

export interface LibraryProject {
  /** The project's address: its id for the account's own, `<owner>/<projectId>` for one shared with it. */
  id: string;
  key: Uint8Array;
  summary: ProjectSummary | null;
  info: ProjectInfo;
  createdAt: number;
  /** Shared with this account (absent for its own). */
  shared?: SharedInfo;
  /** (the owner's own) A member was removed or left: the key should change (PROTOCOL.md §10.7). */
  needsRotation?: boolean;
}

/** A share accepted but not yet confirmed by its owner: listed, not openable. */
export interface PendingShare {
  id: string;
  owner: string;
  ownerName: string;
  ownerPublicKey: string | null;
  projectId: string;
  role: ShareRole;
}

/** This account's role in a project. */
export const roleOf = (p: Pick<LibraryProject, 'shared'> | null | undefined): 'owner' | ShareRole => p?.shared?.role ?? 'owner';

export async function fetchProjects(api: Api): Promise<WebProject[]> {
  return (await api.get<{ projects: WebProject[] }>('/api/projects')).projects;
}

/** Opens each project's key and summary; a project whose key won't open (another vault's) is left out. */
export async function openProjects(list: WebProject[], accountId: string, avk: Uint8Array, store: OfflineStore): Promise<LibraryProject[]> {
  const opened = await Promise.all(
    list.map(async (p): Promise<LibraryProject | null> => {
      try {
        const key = await unwrapProjectKey(avk, accountId, p.id, fromB64u(p.wrappedKey));
        const summary = p.summary ? await openSummary(key, fromB64u(p.summary)).catch(() => null) : null;
        const needsRotation = !!(p as WebProject & { needsRotation?: boolean }).needsRotation;
        return { id: p.id, key, summary, info: await store.info(p.id), createdAt: p.createdAt, ...(needsRotation ? { needsRotation } : {}) };
      } catch {
        return null;
      }
    }),
  );
  return opened.filter((p): p is LibraryProject => p !== null);
}

// Shared with me ------------------------------------------------------------------------------------------------------

export async function fetchShares(api: Api): Promise<Share[]> {
  return listShares(api);
}

/**
 * The relay's shares merged with the ones this computer kept: a share whose wraps the relay no longer gives (removed,
 * deleted, or gone from its list) keeps the wraps and summary kept here, so its copy still opens for exporting. A
 * project this account left is dropped by the caller (`forgetShare`).
 */
export function mergeShares(kept: Share[] | null, fresh: Share[]): Share[] {
  const key = (s: Pick<Share, 'owner' | 'projectId'>) => addressOf(s.owner, s.projectId);
  const before = new Map((kept ?? []).map((s) => [key(s), s]));
  const out: Share[] = [];
  const seen = new Set<string>();
  for (const s of fresh) {
    seen.add(key(s));
    const had = before.get(key(s));
    if (s.status !== 'active' && had && (had.avkWrap || had.memberWrap)) {
      out.push({ ...had, status: s.status, ownerName: s.ownerName || had.ownerName });
    } else out.push(s);
  }
  for (const [k, s] of before) {
    if (seen.has(k)) continue;
    // Gone from the relay's list after 30 days, or never told: still readable here while this computer keeps it.
    if (s.avkWrap || s.memberWrap) out.push({ ...s, status: s.status === 'active' ? 'removed' : s.status });
  }
  return out;
}

/** Opens the shares: active and kept ones with their keys (stored AVK copy first, else the member wrap), pending ones
 *  listed. `api` null offline (no AVK copy is stored then). */
export async function openShares(
  shares: Share[],
  o: { api: Api | null; accountId: string; avk: Uint8Array; exchange: () => Promise<ExchangeKey | null>; store: OfflineStore },
): Promise<{ projects: LibraryProject[]; pending: PendingShare[]; shares: Share[] }> {
  const projects: LibraryProject[] = [];
  const pending: PendingShare[] = [];
  const kept: Share[] = [];
  for (const s of shares) {
    const id = addressOf(s.owner, s.projectId);
    const base = { owner: s.owner, ownerName: s.ownerName ?? '', ownerPublicKey: s.ownerPublicKey ?? null, projectId: s.projectId, role: s.role };
    if (s.status === 'pending') {
      pending.push({ id, ...base });
      kept.push(s);
      continue;
    }
    try {
      const opened = await openShare(s.status === 'active' ? o.api : null, o.accountId, o.avk, o.exchange, s);
      kept.push(opened.raw);
      if (!opened.key) continue;
      projects.push({
        id, key: opened.key, summary: opened.summary, info: await o.store.info(id), createdAt: s.invitedAt ?? 0,
        shared: { ...base, status: s.status === 'active' ? 'active' : s.status === 'deleted' ? 'deleted' : 'removed' },
      });
    } catch {
      kept.push(s);
    }
  }
  return { projects, pending, shares: kept };
}

/** What the Library calls a project: its decrypted summary's title (what the uploading device named it), else the core's. */
export const projectTitle = (p: LibraryProject) => p.summary?.title || p.info.title || 'Untitled project';
export const projectSeries = (p: LibraryProject) => (p.info.title ? (p.info.series ?? null) : (p.summary?.series ?? null)) || null;
